import { Toolbar, Panel, View, f7 } from 'framework7-react';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import AuthState from '../atoms/AuthState.jsx';
import '../css/toolbar.scss';
import { removeLoginData, getUserData } from '../utils/auth';
import useAuthenticate from '../actions/Authenticate';
import SignupPopup from './signupPopup';
import { SidebarContent, SOCIAL_LINKS } from './SidePanel';
import LanguagePopup from './LanguagePopup';

const LOGIN_REQUIRED_PATHS = ['/transaction', '/promotion', '/profile', '/reward', '/deposit', '/withdraw'];
const MOBILE_MAX_WIDTH = 500;
const isLoginRequired = (path) => LOGIN_REQUIRED_PATHS.includes(path);

const ME_ACTIVE_PATHS = [
    '/profile',
    '/profile-details',
    '/change-password',
    '/referral',
    '/messaging',
    '/daily-mission',
];

const formatNotificationTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const ToolbarComponent = () => {
    const authState = useRecoilValue(AuthState);
    const isLoggedIn = !!authState?.token;
    const navigate = useNavigate();
    const { logout, notifications: fetchNotifications } = useAuthenticate();
    const location = useLocation();
    const { i18n } = useTranslation();
    const [activeMenuItem, setActiveMenuItem] = useState('Home');
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);
    const [showSignupPopup, setShowSignupPopup] = useState(false);
    const [socialListOpen, setSocialListOpen] = useState(false);
    const [notificationPopupOpen, setNotificationPopupOpen] = useState(false);
    const [notificationList, setNotificationList] = useState([]);
    const [socialPanelPosition, setSocialPanelPosition] = useState(null);
    const [notificationPanelPosition, setNotificationPanelPosition] = useState(null);
    const [isMobileView, setIsMobileView] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH);
    const socialTriggerRef = useRef(null);
    const notificationTriggerRef = useRef(null);

    const currentLanguageCode = (i18n.language || i18n.resolvedLanguage || '').split('-')[0];

    const getMenuItemFromPath = (path) => {
        if (path === '/' || path === '/home') return 'Home';
        const pathMap = {
            '/casino': 'Casino',
            '/slots': 'Slots',
            // '/sports': 'Sports',
            // '/poker': 'Poker',
            // '/fast-games': 'Fast Games',
            '/promotion': 'Promo',
            '/reward': 'Reward',
            '/vip-ranking': 'VIP Ranking',
            '/profile': 'Profile',
            '/transaction': 'History',
        };
        return pathMap[path] || 'Home';
    };

    useEffect(() => {
        setActiveMenuItem(getMenuItemFromPath(location.pathname));
    }, [location.pathname]);

    const loadNotifications = async () => {
        const res = await fetchNotifications();
        if (res.status && Array.isArray(res.data)) {
            const maxItems = 10;
            const slicedList = res.data.slice(0, maxItems).map((item) => {
                const n = item.notification || item;
                const title = n.title_json ?? n.title ?? {};
                const body = n.message_json ?? n.message ?? {};
                return { title, body, created_at: n.created_at ?? n.date };
            });
            setNotificationList(slicedList);
        } else {
            setNotificationList([]);
        }
    };

    useEffect(() => {
        if (isLoggedIn && notificationPopupOpen) {
            loadNotifications();
        } else if (!isLoggedIn) {
            setNotificationList([]);
        }
    }, [isLoggedIn, notificationPopupOpen]);

    useEffect(() => {
        if (!socialListOpen) return;
        const handleEscape = (e) => {
            if (e.key === 'Escape') setSocialListOpen(false);
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [socialListOpen]);

    useEffect(() => {
        if (!notificationPopupOpen) return;
        const handleEscape = (e) => {
            if (e.key === 'Escape') setNotificationPopupOpen(false);
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [notificationPopupOpen]);

    useEffect(() => {
        if (!socialListOpen) {
            setSocialPanelPosition(null);
            return;
        }
        const el = socialTriggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const moveUp = 100;
        const minTop = 16;
        const maxTop = Math.max(minTop, window.innerHeight - 320);
        const top = Math.min(maxTop, Math.max(minTop, rect.top - moveUp));
        const arrowOffset = rect.top - top + rect.height / 2;
        setSocialPanelPosition({ top, arrowOffset });
    }, [socialListOpen]);

    useEffect(() => {
        const checkMobile = () => setIsMobileView(window.innerWidth <= MOBILE_MAX_WIDTH);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // On mobile: center notification popup; on desktop: position near trigger
    useEffect(() => {
        if (!notificationPopupOpen) {
            setNotificationPanelPosition(null);
            return;
        }
        if (isMobileView) {
            setNotificationPanelPosition(null); // centered via CSS
            return;
        }
        const el = notificationTriggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const moveUp = 80;
        const minTop = 16;
        const maxTop = Math.max(minTop, window.innerHeight - 380);
        const top = Math.min(maxTop, Math.max(minTop, rect.top - moveUp));
        const arrowOffset = rect.top - top + rect.height / 2;
        setNotificationPanelPosition({ top, arrowOffset });
    }, [notificationPopupOpen, isMobileView]);

    // Handle body overflow when signup popup is open
    useEffect(() => {
        if (showSignupPopup) {
            // Ensure body is scrollable when popup is open
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
        }
    }, [showSignupPopup]);

    // Helper function to reset backdrop filters
    const resetBackdropFilters = () => {
        const backdrops = document.querySelectorAll('.panel-backdrop, .panel-overlay, [class*="panel-backdrop"], [class*="panel-overlay"]');
        backdrops.forEach(backdrop => {
            if (!backdrop.classList.contains('panel-backdrop-in') && !backdrop.classList.contains('panel-overlay-in')) {
                // Remove all backdrop filter styles
                backdrop.style.removeProperty('backdrop-filter');
                backdrop.style.removeProperty('-webkit-backdrop-filter');
                backdrop.style.backdropFilter = 'none';
                backdrop.style.webkitBackdropFilter = 'none';
                backdrop.style.setProperty('backdrop-filter', 'none', 'important');
                backdrop.style.setProperty('-webkit-backdrop-filter', 'none', 'important');
            }
        });
    };

    // Close right panel and backdrop when viewport grows past mobile breakpoint (toolbar hides at 501px)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > MOBILE_MAX_WIDTH) {
                f7.panel.close('#right-panel');
                resetBodyStyles();
                resetBackdropFilters();
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Prevent body scroll when panel is open
    useEffect(() => {
        const handlePanelOpen = () => {
            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        };

        const handlePanelClose = () => {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            if (scrollY) {
                const scrollValue = parseInt(scrollY.replace('px', '') || '0') * -1;
                window.scrollTo(0, scrollValue);
            }

            // Reset backdrop filters immediately and after transition
            resetBackdropFilters();
            setTimeout(() => {
                resetBackdropFilters();
            }, 350);
        };

        // Listen for Framework7 panel events
        f7.on('panelOpen', handlePanelOpen);
        f7.on('panelClose', handlePanelClose);

        // Also listen to panel element directly
        const panelElement = document.getElementById('right-panel');
        if (panelElement) {
            panelElement.addEventListener('panel:open', handlePanelOpen);
            panelElement.addEventListener('panel:close', handlePanelClose);
        }

        return () => {
            f7.off('panelOpen', handlePanelOpen);
            f7.off('panelClose', handlePanelClose);
            if (panelElement) {
                panelElement.removeEventListener('panel:open', handlePanelOpen);
                panelElement.removeEventListener('panel:close', handlePanelClose);
            }
            // Cleanup on unmount
            handlePanelClose();
        };
    }, []);

    const handleMenuItemClick = (menuItem, path) => {
        f7.panel.close('#right-panel');
        setIsLanguageOpen(false);
        navigate(path);
        setActiveMenuItem(menuItem);
    };

    const closePanel = () => f7.panel.close('#right-panel');

    const isActive = (path) => {
        if (path === '/' || path === '/home') return location.pathname === '/' || location.pathname === '/home';
        if (path === '/profile') return ME_ACTIVE_PATHS.includes(location.pathname) || location.pathname.startsWith('/profile/');
        return location.pathname.startsWith(path);
    };

    const handleNav = (path) => {
        closePanel();
        navigate(path);
    };

    const handleLoginClick = () => {
        closePanel();
        window.dispatchEvent(new Event('openLoginPopup'));
    };

    const handleLoginRequired = () => handleLoginClick();

    const handleSignOut = async () => {
        try {
            await logout();
            removeLoginData();
            if (typeof f7 !== 'undefined' && f7.toast) {
                f7.toast.create({ text: 'Logout successful', closeTimeout: 2000 }).open();
            }
            closePanel();
            navigate('/');
        } catch (e) {
            console.error(e);
        }
    };

    const toggleLanguage = () =>
        setIsLanguageOpen((prev) => {
            const next = !prev;
            if (next) {
                // When opening language popup, ensure side menu is closed
                closePanel();
            }
            return next;
        });
    // Helper function to reset body styles
    const resetBodyStyles = () => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        if (scrollY) {
            const scrollValue = parseInt(scrollY.replace('px', '') || '0') * -1;
            window.scrollTo(0, scrollValue);
        }
    };

    // const openSignupPopup = () => setShowSignupPopup(true);
    const openSignupPopup = () => {
        f7.panel.close('#right-panel');
        // Reset backdrop filters and body styles after panel closes
        setTimeout(() => {
            resetBackdropFilters();
            resetBodyStyles();
            setShowSignupPopup(true);
        }, 100);
    };

    const closeSignupPopup = () => {
        setShowSignupPopup(false);
        // Reset backdrop filters before reopening panel
        resetBackdropFilters();
        setTimeout(() => {
            f7.panel.open('#right-panel');
        }, 50);
    };

    const userData = isLoggedIn ? (authState?.user ?? getUserData()) : null;
    const userDisplayName = userData ? (userData.full_name ?? userData.username ?? 'User') : '';
    const userDisplayId = userData ? (userData.userId ?? userData.id ?? userData.code ?? userData.username ?? '') : '';

    const sidebarContentProps = {
        isExpanded: true,
        isActive,
        isLoggedIn,
        onNav: handleNav,
        onLoginClick: handleLoginClick,
        onSignOut: handleSignOut,
        showLabels: true,
        showCloseButton: true,
        onClose: closePanel,
        showToggle: false,
        onToggleExpand: () => { },
        onToggleCollapse: () => { },
        onOpenSocialList: () => setSocialListOpen(true),
        socialTriggerRef,
        onOpenNotificationPopup: () => {
            // On mobile: close side menu first, then show notification centered
            if (typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH) {
                f7.panel.close('#right-panel');
                setTimeout(() => {
                    resetBackdropFilters();
                    resetBodyStyles();
                    setNotificationPopupOpen(true);
                }, 350);
            } else {
                setNotificationPopupOpen(true);
            }
        },
        notificationTriggerRef,
        onLoginRequired: handleLoginRequired,
        onToggleLanguage: toggleLanguage,
        currentLanguageCode,
        userDisplayName,
        userDisplayId,
    };

    return (
        <>
            <div className="toolbar-container">
                <div
                    className={`chat-bot ${isActive('/live_chat') ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNav('/live_chat')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleNav('/live_chat');
                        }
                    }}
                    aria-label="24/7 Support"
                >
                    <img src="/images/chat.png" draggable={false} alt="" />
                </div>

                <Toolbar tabbar className="toolbar-wrapper">
                    <div className={`toolbar-item ${isActive('/') ? 'active' : ''}`} onClick={() => navigate('/')}>
                        <div className="toolbar-icon"><img src="/images/home.png" draggable={false} /></div>
                        <span className="font-medium">Home</span>
                    </div>
                    {/* <div className={`toolbar-item ${isActive('/sports') ? 'active' : ''}`} onClick={() => navigate('/sports')}>
                        <div className="toolbar-icon"><img src="/images/sports.png" draggable={false} /></div>
                        <span className="font-medium">Sports</span>
                    </div> */}
                    <div className={`toolbar-item ${isActive('/transaction') ? 'active' : ''}`} onClick={() => navigate('/transaction')}>
                        <div className="toolbar-icon"><img src="/images/history.png" draggable={false} /></div>
                        <span className="font-medium">History</span>
                    </div>
                    <div className={`toolbar-item ${isActive('/promotion') ? 'active' : ''}`} onClick={() => navigate('/promotion')}>
                        <div className="toolbar-icon"><img src="/images/promo.png" draggable={false} /></div>
                        <span className="font-medium">Promo</span>
                    </div>
                    {/* <div className={`toolbar-item ${isActive('/casino') ? 'active' : ''}`} onClick={() => navigate('/casino')}>
                        <div className="toolbar-icon"><img src="/images/casino.png" draggable={false} /></div>
                        <span className="font-medium">Casino</span>
                    </div> 
                    <div className={`toolbar-item ${isActive('/slots') ? 'active' : ''}`} onClick={() => navigate('/slots')}>
                        <div className="toolbar-icon"><img src="/images/slots.png" draggable={false} /></div>
                        <span className="font-medium">Slots</span>
                    </div>*/}

                    <div className={`toolbar-item ${isActive('/profile') ? 'active' : ''}`} onClick={() => navigate('/profile')}>
                        <div className="toolbar-icon"><img src="/images/profile.png" draggable={false} /></div>
                        <span className="font-medium">Profile</span>
                    </div>
                    {/* <div className="toolbar-item" onClick={() => f7.panel.open('right')}>
                        <div className="toolbar-icon"><img src="/images/menu.png" draggable={false} /></div>
                        <span className="font-medium">Menu</span>
                    </div> */}
                    <div className="toolbar-item" onClick={() => f7.panel.open('right')}>
                        <div className="toolbar-icon"><img src="/images/menu.png" draggable={false} /></div>
                        <span className="font-medium">Menu</span>
                    </div>
                </Toolbar>
            </div>

            {/* Framework7 Right Panel */}
            {/* color panel */}
            {/* <Panel side="right" themeDark backdrop id="right-panel">
                <View>
                    <div className="sidebar-menu">
                        <div className="sidebar-header">
                            <div className="close-button" onClick={() => f7.panel.close('#right-panel')}>
                                <i className="f7-icons font-xx-large font-bold white">xmark</i>
                            </div>
                        </div>

                        <div className="sidebar-content">
                            {isLoggedIn ? (
                                <div className="user-profile-section" onClick={() => handleMenuItemClick('Profile', '/profile')}>
                                    <div className="user-avatar"><img src="/images/user-icon.jpg" alt="" /></div>
                                    <div className="user-info">
                                        <span className="username font-medium font-bold white">{displayName || 'User'}</span>
                                        {displayId ? <span className="user-id font-small white">ID: {displayId}</span> : null}
                                    </div>
                                    <div className="profile-arrow"><i className="f7-icons font-large font-bold white">chevron_right</i></div>
                                </div>
                            ) : (
                                <div className="signup-section" onClick={() => { openSignupPopup() }}>
                                    <div className="signup-icon"><img src="/images/profile-icon.png" /></div>
                                    <div className="signup-button"><span className="font-large font-bold white">Sign Up</span></div>
                                </div>
                            )}

                            <div className="ranking-prizes-container">
                                <div className="prizes-container" onClick={() => handleMenuItemClick('Daily Prizes', '/daily-prizes')}>
                                    <div className="prizes-title"><span className="font-large white">Daily Prizes</span></div>
                                    <div className="prizes-icon"><img src="/images/spinwheel.png" draggable={false} /></div>
                                </div>
                                <div className="ranking-container" onClick={() => handleMenuItemClick('VIP Ranking', '/vip-ranking')}>
                                    <div className="ranking-title"><span className="font-large white">VIP Ranking</span></div>
                                    <div className="ranking-icon"><img src="/images/crown.png" draggable={false} /></div>
                                </div>
                            </div>

                            <div className="navigation-menu">
                                {['Home', 'History', 'Promo'].map(item => (
                                    <div key={item} className={`menu-item white ${activeMenuItem === item ? 'active' : ''}`}
                                        onClick={() => handleMenuItemClick(item, `/${item.toLowerCase().replace(' ', '-')}`)}>
                                        <span className="font-large">{item}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="application-section">
                                <div className="app-content">
                                    <div className="app-text">
                                        <span className="app-title font-medium font-bold">Application</span>
                                        <span className="app-desc font-small">Install the app and get 200 Twin Coins bonuses.</span>
                                    </div>
                                    <div className="app-image">
                                        <div className="item-image">
                                            <img src="/images/application.png" alt="Application" draggable={false} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="support-section">
                                <div className="support-content">
                                    <div className="support-text">
                                        <span className="support-title font-medium font-bold">Support</span>
                                        <span className="support-desc font-small">24/7</span>
                                        <div className="telegram-button"><img src="/images/telegram.png" draggable={false} alt="Telegram" /></div>
                                    </div>
                                    <div className="support-icon">
                                        <div className="item-image">
                                            <img src="/images/support.png" alt="Support" draggable={false} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bottom-section">
                                <div
                                    className="logout-button"
                                    onClick={async () => {
                                        await logout();
                                        removeLoginData();
                                        if (typeof f7 !== 'undefined' && f7.toast) {
                                            f7.toast.create({ text: 'Logout successful', closeTimeout: 2000 }).open();
                                        }
                                        f7.panel.close('#right-panel');
                                        navigate('/');
                                    }}
                                >
                                    <span className="font-large font-bold white">Log out</span>
                                </div>
                                <div className="language-selector" onClick={() => { toggleLanguage() }}>
                                    <img src={languages.find(l => l.name === selectedLanguage)?.flag || '/images/flag-en.png'} />
                                    <div className="dropdown-icon"><img src="/images/chevron_down.png" draggable={false} /></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </View>
            </Panel> */}

            <Panel side="right" themeDark backdrop id="right-panel">
                <View>
                    <div className="toolbar-side-menu web-side-panel web-side-panel-expanded">
                        <SidebarContent {...sidebarContentProps} />
                    </div>
                </View>
            </Panel>

            {socialListOpen && (
                <div
                    className="web-side-panel-social-list-overlay"
                    onClick={() => setSocialListOpen(false)}
                    onKeyDown={(e) => e.key === 'Escape' && setSocialListOpen(false)}
                    role="presentation"
                    aria-hidden
                >
                    <div
                        className="web-side-panel-social-list-panel"
                        style={
                            socialPanelPosition
                                ? { top: socialPanelPosition.top, ['--social-list-arrow-top']: `${socialPanelPosition.arrowOffset}px` }
                                : undefined
                        }
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-label="Social media"
                    >
                        <div className="web-side-panel-social-list-header">
                            <span className="web-side-panel-social-list-title">Social media</span>
                            <div
                                type="button"
                                className="web-side-panel-social-list-close"
                                onClick={() => setSocialListOpen(false)}
                                aria-label="Close"
                            >
                                <i className="f7-icons font-large font-bold">xmark</i>
                            </div>
                        </div>
                        <div className="web-side-panel-social-list-body">
                            {SOCIAL_LINKS.map((s) => (
                                <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" className="web-side-panel-social-list-item">
                                    <img src={s.icon} alt="" />
                                    <span>{s.name}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {notificationPopupOpen && (
                <div
                    className="web-side-panel-social-list-overlay web-side-panel-notification-overlay"
                    onClick={() => setNotificationPopupOpen(false)}
                    onKeyDown={(e) => e.key === 'Escape' && setNotificationPopupOpen(false)}
                    role="presentation"
                    aria-hidden
                >
                    <div
                        className={`web-side-panel-social-list-panel web-side-panel-notification-panel web-side-panel-notification-panel-expanded${notificationPanelPosition == null ? ' web-side-panel-notification-panel-mobile-centered' : ''}`}
                        style={
                            notificationPanelPosition
                                ? { top: notificationPanelPosition.top, ['--social-list-arrow-top']: `${notificationPanelPosition.arrowOffset}px` }
                                : undefined
                        }
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-label="Notifications"
                    >
                        <div className="web-side-panel-social-list-header">
                            <span className="web-side-panel-social-list-title">Notifications</span>
                            <div
                                type="button"
                                className="web-side-panel-social-list-close"
                                onClick={() => setNotificationPopupOpen(false)}
                                aria-label="Close"
                            >
                                <i className="f7-icons font-large font-bold">xmark</i>
                            </div>
                        </div>
                        <div className="web-side-panel-notification-body">
                            {notificationList.length > 0 ? (
                                notificationList.map((notification, idx) => {
                                    const title = notification.title?.[currentLanguageCode] || notification.title?.en || '';
                                    const bodyRaw = notification.body?.[currentLanguageCode] || notification.body?.en || '';
                                    const preview = typeof bodyRaw === 'string' ? bodyRaw : (bodyRaw?.text || '');
                                    const shortPreview = preview.length > 60 ? `${preview.slice(0, 60)}…` : preview;
                                    return (
                                        <div key={idx} className="web-side-panel-notification-item">
                                            <div className="web-side-panel-notification-avatar">
                                                <i className="f7-icons">bell_fill</i>
                                            </div>
                                            <div className="web-side-panel-notification-content">
                                                <div className="web-side-panel-notification-title">{title || 'Notification'}</div>
                                                {shortPreview ? <div className="web-side-panel-notification-preview">{shortPreview}</div> : null}
                                                <div className="web-side-panel-notification-time">{formatNotificationTime(notification.created_at)}</div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="web-side-panel-notification-empty">No notifications yet</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <LanguagePopup opened={isLanguageOpen} onClose={toggleLanguage} />

            {showSignupPopup && <SignupPopup onClose={closeSignupPopup} />}
        </>
    );
};

export default ToolbarComponent;
